import { EventIdentifier, FeatureTeamEvent, TrackingEventType, useAnalytics, useIntl, values } from '@capsicum/common';
import { Button, Display2, useRouter } from '@capsicum/common-web';
import {
	type EmailSMSLoginFormSubmit,
	type EmailSMSLoginFormType,
	showEmailInputFormAtom,
	useAttemptErrors,
	useAttemptSchema,
	useEmailFieldValidation,
	useEmailSMSLoginForm,
	useMobileNumberFieldValidation,
	useSendCodeByEmail,
	useSendCodeBySMS,
	type WelcomeFormattedMedium
} from '@capsicum/socios-core';
import { useAtomValue } from 'jotai';
import Link from 'next/link';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';

import { useTriggerCaptcha } from '../../hooks/useTriggerCaptcha';
import { type OnCodeVerificationRedirection, type WelcomeProps } from '../../models';
import { Shell } from '../../ui/Shell';
import GeeTestContainer, { type GeeTestRef } from '../user-details/GeeTestContainer/GeeTestContainer';

import { EmailPhoneInput } from './EmailPhoneInput';

type UserDetailsProps = {
	formattedValues?: WelcomeFormattedMedium;
	onStageChange: OnCodeVerificationRedirection;
	variant: WelcomeProps['variant'];
};

export const UserDetails = ({ formattedValues, onStageChange, variant }: UserDetailsProps) => {
	const analytics = useAnalytics();
	const { $t } = useIntl();
	const { searchParams } = useRouter();
	const sendCodeBySMS = useSendCodeBySMS();
	const attemptSchema = useAttemptSchema();
	const [attempt, setAttempt] = useState(0);
	const welcomeForm = useEmailSMSLoginForm({ formattedValues, variant });
	const captchaRef = useRef<GeeTestRef>(null);
	const sendCodeByEmail = useSendCodeByEmail();
	const attemptError = useAttemptErrors(attempt);
	const [geetestReady, setGeetestReady] = useState(false);
	const validateEmail = useEmailFieldValidation(welcomeForm);
	const showEmailInput = useAtomValue(showEmailInputFormAtom);
	const validatePhoneNumber = useMobileNumberFieldValidation(welcomeForm);
	const triggerCaptcha = useTriggerCaptcha({ captcha: captchaRef.current });
	const id = welcomeForm.watch('id');

	const debouncedErrorEvent = useDebouncedCallback(
		(errorMessage = '') => {
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFormError, {
				extra_parameter: `Value: ${id} Error: ${errorMessage}`,
				feature_team: FeatureTeamEvent.ORFT,
				page: `${variant}_email_phone_page`,
				text: 'email_phone_error_triggered'
			});
		},
		2500,
		{ leading: true, trailing: false }
	);

	const allErrors = useMemo(
		() => [
			...values(welcomeForm.formState.errors).map(v => {
				debouncedErrorEvent(v.message);

				return v.message !== $t('auth.emailTaken')
					? v.message
					: $t('auth.emailTakenWithLoginLink', {
							link: (chunks: ReactNode) => <S.LinkVariant href="/login">{chunks}</S.LinkVariant>
						});
			}),
			...(!attemptError.success ? [attemptError.error.issues?.[0]?.message] : [])
		],
		[$t, welcomeForm.formState, attemptError, debouncedErrorEvent]
	);

	const onSendCodeBySMS = useCallback(
		async ({
			countryCode,
			mobileNumber
		}: {
			countryCode: EmailSMSLoginFormType['country']['iso2'];
			mobileNumber: EmailSMSLoginFormType['id'];
		}) =>
			sendCodeBySMS({
				attempt: String(attempt),
				captchaInformation: await triggerCaptcha({
					metadata: {
						details: {
							countryCode,
							mobileNumber
						},
						type: 'phone-number'
					}
				}),
				countryCode,
				mobileNumber,
				variant
			}),
		[sendCodeBySMS, attempt, triggerCaptcha, variant]
	);

	const onSendCodeByEmail = useCallback(
		async ({ email }: { email: EmailSMSLoginFormType['id'] }) =>
			sendCodeByEmail({
				attempt: String(attempt),
				captchaInformation: await triggerCaptcha(),
				email,
				variant
			}),
		[sendCodeByEmail, attempt, triggerCaptcha, variant]
	);

	const onEmailLogin = useCallback<EmailSMSLoginFormSubmit>(
		async ({ id: email }) => {
			const emailDetails = await validateEmail({ email });

			if (!emailDetails || !emailDetails.isValid) return;

			if (emailDetails.isAvailable) {
				welcomeForm.setError('id', {
					message: $t('auth.welcome.login.errors.email.no-account'),
					type: 'api'
				});

				return;
			}

			await onSendCodeByEmail({ email });

			onStageChange({
				medium: emailDetails,
				type: 'email'
			});
		},
		[$t, validateEmail, welcomeForm, onStageChange, onSendCodeByEmail]
	);

	const onPhoneNumberLogin = useCallback<EmailSMSLoginFormSubmit>(
		async ({ country: { iso2: countryCode }, id: mobileNumber }) => {
			const phoneNumberDetails = await validatePhoneNumber({ countryCode, mobileNumber });

			if (!phoneNumberDetails || !phoneNumberDetails.isValid) return;

			if (phoneNumberDetails.isAvailable) {
				welcomeForm.setError('id', {
					message: $t('auth.welcome.login.errors.phone-number.no-account'),
					type: 'api'
				});

				return;
			}

			await onSendCodeBySMS({ countryCode, mobileNumber });

			onStageChange({
				medium: phoneNumberDetails,
				type: 'phone-number'
			});
		},
		[$t, onSendCodeBySMS, onStageChange, validatePhoneNumber, welcomeForm]
	);

	const onEmailRegister = useCallback<EmailSMSLoginFormSubmit>(
		async ({ id: email }) => {
			const emailDetails = await validateEmail({ email });

			if (!emailDetails || !emailDetails.isValid) {
				analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFormError, {
					extra_parameter: id,
					feature_team: FeatureTeamEvent.ORFT,
					page: `${variant}_email_phone_page`,
					text: 'register_email_not_valid'
				});

				return;
			}

			if (!emailDetails.isAvailable) {
				analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFormError, {
					extra_parameter: id,
					feature_team: FeatureTeamEvent.ORFT,
					page: `${variant}_email_phone_page`,
					text: 'register_email_in_use'
				});

				welcomeForm.setError('id', {
					message: $t('auth.welcome.register.errors.email.in-use'),
					type: 'api'
				});

				return;
			}

			await onSendCodeByEmail({ email });

			onStageChange({
				medium: emailDetails,
				type: 'email'
			});
		},
		[$t, validateEmail, welcomeForm, onStageChange, onSendCodeByEmail, analytics]
	);

	const onPhoneNumberRegister = useCallback<EmailSMSLoginFormSubmit>(
		async ({ country: { iso2: countryCode }, id: mobileNumber }) => {
			const phoneNumberDetails = await validatePhoneNumber({ countryCode, mobileNumber });

			if (!phoneNumberDetails || !phoneNumberDetails.isValid) {
				analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFormError, {
					extra_parameter: `${countryCode} ${mobileNumber}`,
					feature_team: FeatureTeamEvent.ORFT,
					page: `${variant}_email_phone_page`,
					text: 'register_phone_not_valid'
				});

				return;
			}

			if (!phoneNumberDetails.isAvailable) {
				analytics.sendEvent(TrackingEventType.track, EventIdentifier.OnboardingFormError, {
					extra_parameter: `${countryCode} ${mobileNumber}`,
					feature_team: FeatureTeamEvent.ORFT,
					page: `${variant}_email_phone_page`,
					text: 'register_phone_already_used'
				});

				welcomeForm.setError('id', {
					message: $t('auth.welcome.register.errors.phone-number.in-use'),
					type: 'api'
				});

				return;
			}

			await onSendCodeBySMS({ countryCode, mobileNumber });

			onStageChange({
				medium: phoneNumberDetails,
				type: 'phone-number'
			});
		},
		[$t, onSendCodeBySMS, onStageChange, validatePhoneNumber, welcomeForm, analytics]
	);

	const login = useCallback<EmailSMSLoginFormSubmit>(
		async (...args) => {
			if (attemptSchema.safeParse(attempt + 1).success) {
				await (showEmailInput ? onEmailLogin(...args) : onPhoneNumberLogin(...args));
			}

			setAttempt(x => ++x);
		},
		[showEmailInput, onEmailLogin, onPhoneNumberLogin, attemptSchema, attempt]
	);

	const register = useCallback<EmailSMSLoginFormSubmit>(
		async (...args) => (showEmailInput ? onEmailRegister(...args) : onPhoneNumberRegister(...args)),
		[showEmailInput, onEmailRegister, onPhoneNumberRegister]
	);

	const onSubmit = useCallback<EmailSMSLoginFormSubmit>(
		(...args) => (variant === 'login' ? login(...args) : register(...args)),
		[variant, login, register]
	);

	useEffect(
		() =>
			analytics.sendEvent(TrackingEventType.track, EventIdentifier.PageView, {
				feature_team: FeatureTeamEvent.ORFT,
				page: `${variant}_email_phone_page`
			}),
		[analytics]
	);

	return (
		<FormProvider {...welcomeForm}>
			<S.Form onSubmit={welcomeForm.handleSubmit(onSubmit)}>
				<Shell
					buttons={[
						<Button
							disabled={!geetestReady || !id || id.length < 1 || welcomeForm.formState.isSubmitting}
							fullWidth
							isLoading={!geetestReady}
							key="confirm"
							testID="login-confirm-button"
							text={$t('auth.sendFirstCodeCta')}
						/>
					]}
				>
					<S.Header>
						<div>
							<S.Title>{$t('auth.WelcomeTo')}</S.Title>
							<S.BlueTitle>Socios.com</S.BlueTitle>
						</div>

						<S.PageTitle>
							{$t(variant === 'login' ? 'auth.welcome.login.title' : 'auth.welcome.register.title')}
						</S.PageTitle>
					</S.Header>

					<S.Container>
						<S.EmailPhoneContainer>
							<S.EmailPhoneField>
								<S.EmailPhoneLabel data-testid="login-phone-number-label">
									{$t('auth.phoneNumberLabel')}
								</S.EmailPhoneLabel>

								<EmailPhoneInput testID="login-emailPhoneNumber" />
							</S.EmailPhoneField>

							{allErrors?.[0] && <S.SupportingInputInfo>{allErrors?.[0]}</S.SupportingInputInfo>}
						</S.EmailPhoneContainer>

						<S.ToggleVariant>
							{$t<JSX.Element>(
								variant === 'login' ? 'auth.welcome.login.toggle' : 'auth.welcome.register.toggle',
								{
									link: chunks => (
										<S.LinkVariant
											href={
												(variant === 'login' ? '/register' : '/login') +
												`?${searchParams.toString()}`
											}
										>
											{chunks}
										</S.LinkVariant>
									)
								}
							)}
						</S.ToggleVariant>
					</S.Container>
				</Shell>
			</S.Form>

			<GeeTestContainer ref={captchaRef} onReady={() => setGeetestReady(true)} />
		</FormProvider>
	);
};

namespace S {
	export const Header = styled.header`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.medium};
		margin-bottom: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const Title = styled(Display2)`
		text-transform: uppercase;
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			display: inline;
		}
	`;

	export const BlueTitle = styled(Title)`
		color: ${({ theme }) => theme.colors.backgrounds.brand.enabled};
		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			margin-left: 0.4em;
			display: inline;
		}
	`;

	export const PageTitle = styled.div`
		${({ theme }) => theme.fonts.text.textTopBar};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ToggleVariant = styled.div`
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const LinkVariant = styled(Link)`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const Form = styled.form`
		width: 100%;
		height: 100%;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const EmailPhoneContainer = styled.div`
		display: flex;
		flex-direction: column;
		height: 100%;
		flex-grow: 1;
	`;

	export const EmailPhoneField = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const EmailPhoneLabel = styled.div`
		${({ theme }) => theme.fonts.text.text5}
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const SupportingInputInfo = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.borders.error.enabled};
		margin-bottom: ${({ theme }) => theme.spacings.xxsmall};
	`;
}
